import { apiDelete, apiGet, apiPost, apiPut, ApiError } from '../../lib/apiClient';
import type { PromptDetail, PromptPreview, PromptSummary, PromptValidationIssue, PromptVersion } from './types';

const BASE = '/ai-prompts/admin';

export const listPrompts = () =>
  apiGet<{ prompts: PromptSummary[] }>(BASE).then((r) => r.prompts);

export const readPrompt = (key: string) => apiGet<PromptDetail>(`${BASE}/${key}`);

export const previewPrompt = (
  key: string,
  body: { en?: string; es?: string; language: string; sampleVariables?: Record<string, string> },
) => apiPost<PromptPreview>(`${BASE}/${key}/preview`, body);

export const createVersion = (
  key: string,
  body: { en?: string; es?: string; label?: string; note?: string; fromVersionId?: string },
) => apiPost<PromptVersion>(`${BASE}/${key}/versions`, body);

export const updateVersion = (
  key: string,
  versionId: string,
  body: { en?: string; es?: string; label?: string; note?: string },
) => apiPut<PromptVersion>(`${BASE}/${key}/versions/${versionId}`, body);

export const publishVersion = (key: string, versionId: string) =>
  apiPost<PromptVersion>(`${BASE}/${key}/versions/${versionId}/publish`);

export const unpublishPrompt = (key: string) => apiPost<PromptDetail>(`${BASE}/${key}/unpublish`);

export const deleteVersion = (key: string, versionId: string) =>
  apiDelete<{ deleted: string }>(`${BASE}/${key}/versions/${versionId}`);

/**
 * Pull the per-language reasons out of a rejected save.
 *
 * The backend refuses a prompt that breaks its contract — a dropped
 * `<service_recommendation>` tag, an invented placeholder — and says exactly
 * which language and which rule. Surfacing that verbatim is the whole value:
 * a generic "save failed" would send the operator hunting for a typo the server
 * already found.
 */
export function issuesFrom(error: unknown): PromptValidationIssue[] {
  if (!(error instanceof ApiError)) return [];
  const body = error.errors as { issues?: PromptValidationIssue[] } | undefined;
  return Array.isArray(body?.issues) ? body!.issues : [];
}

export function messageFrom(error: unknown): string {
  return error instanceof ApiError ? error.message : 'Request failed';
}
